import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { IncommingCall } from "../../components/Modals/IncommingCall/IncommingCall";
import { ActionFn, UiModal } from "../../components/Modals/UiModals";
import { env } from "../../env";
import { addModal, clearModal, removeModal } from "../../redux/actions/Modals";
import { addIncommingCall, addUser, removeCall, removeIncommingCall, removeUser, setUsers } from "../../redux/actions/Online";
import { setPage } from "../../redux/actions/Page";
import { ReduxState } from "../../redux/store";
import { routes } from "../../routes";
import "./Online.scss";

export const Online = ({inChat = false}: {inChat?: boolean}) => {
    const dispatch = useDispatch();
    
    const {params} = useSelector(({page}: ReduxState) => page);
    const {call, incommingCall} = useSelector(({online}: ReduxState) => online);
    const [searchParams] = useSearchParams();
    const [sessionId] = useState<string | null>(params?.sessionId ?? searchParams.get('sessionId'));
    const [connection, setConnection] = useState<null | HubConnection>(null);
    const [accepted, setAccepted] = useState<boolean>(false);
    const audioCtxRef = useRef<any>(null);
    const ringIntervalRef = useRef<any>(null);

    useEffect(() => {
        if (sessionId) {
            const connect = new HubConnectionBuilder()
                .withUrl(env().REACT_APP_ONLINE_HUB_URL ?? "")
                .withAutomaticReconnect()
                .build();

            setConnection(connect);
        }
    }, [sessionId]);

    useLayoutEffect(() => {
        return () => {
            if (connection) {
                connection?.stop().then(() => {
                    setConnection(null);
                });
            }
        }
    }, [connection]);

    useEffect(() => {
        if (connection !== null) {
            connection.start().then(() => {
                const request: any = {
                    sessionId
                }
                sendMessageSignal("Add", request);
            });
        }
    }, [connection]);

    useEffect(() => {
        if (connection !== null) {
            connection?.on("OnlineUsersResponse", (message: any) => {
                dispatch(setUsers(message));
            });
            connection?.on("AddOnlineUserResponse", (message: any) => {
                dispatch(addUser(message));
            });
            connection?.on("RemoveOnlineUserResponse", (message: any) => {
                dispatch(removeUser(message));
            });
            connection?.on("IncomingCallResponse", (message: any) => {
                if (message?.message) {
                    dispatch(removeCall());
                    dispatch(clearModal());
                } else {
                    dispatch(addIncommingCall({userId: message.userId, 
                        userName: message.userName,
                        callerId: message.answerId, 
                        callerName: "", 
                        isAudio: message.isAudio, 
                        roomId: message.roomId}));
                }
            });
            connection?.on("AnswerCallResponse", (message: any) => {
                if (message?.message) {
                    dispatch(removeIncommingCall());
                    dispatch(clearModal());
                } else {
                    setAccepted(true);
                }
            });
            connection?.on("DeclineCallResponse", (message: any) => {
                dispatch(removeCall());
                dispatch(clearModal());
                setAccepted(false);
            });
            connection?.on("CancelCallResponse", (message: any) => {
                dispatch(removeIncommingCall());
                dispatch(clearModal());
                stopRinging();
            });
        }
    }, [connection]);

    useEffect(() => {
        if (call) {
            const component = IncommingCall({
                userName: call.userName ?? "",
                isIncomming: false,
                onSuccess: () => {
                    dispatch(removeModal(modal));
                }
            });
            const close: ActionFn = () => {
                sendMessageSignal("CancelCall", {userId: call.userId});
                dispatch(removeModal(modal));
            };
            const modal: UiModal = {component, close};
            dispatch(addModal(modal));
            sendMessageSignal("Call", {callUserId: call.callerId, callUserName: call.callerName, userId: call.userId, isAudio: call.isAudio, roomId: call.roomId});
        }
    }, [call]);
    
    useEffect(() => {
        if (call && accepted && sessionId) {
            if (inChat) {
                let request: any = {sessionId: sessionId, roomId: call.roomId, link: params?.link};
                if (call.isAudio) {
                    request["audioOnly"] = "true";
                    request["directStart"] = "true";
                }
                dispatch(setPage(routes.videoChat, request));
            } else {
                const url = new URL(params?.link ? (params?.link + "/") : (window.location.origin+'/'+env().REACT_APP_ROOT_FOLDER+'/'));
                url.searchParams.append('roomId', call.roomId ?? "");
                url.searchParams.append('sessionId', sessionId ?? "");
                if (call.isAudio) {
                    url.searchParams.append('directStart', "true");
                    url.searchParams.append('audioOnly', "true");
                }
                const blankUrl = `${url.origin}${url.pathname}${url.search}`;
                window.open(blankUrl, '_blank');
            }
            dispatch(removeCall());
            dispatch(clearModal());
            setAccepted(false);
        }
    }, [call, accepted, sessionId])

    useEffect(() => {
        if (incommingCall) {
            const component = IncommingCall({
                userName: incommingCall.userName ?? "",
                isIncomming: true,
                onSuccess: () => {
                    stopRinging();
                    sendMessageSignal("Answer", {answerUserId: incommingCall.callerId, userId: incommingCall.userId});
                    if (inChat) {
                        let request: any = {sessionId: sessionId, roomId: incommingCall.roomId, link: params?.link};
                        if (incommingCall.isAudio) {
                            request["audioOnly"] = "true";
                            request["directStart"] = "true";
                        }
                        dispatch(setPage(routes.videoChat, request));
                    } else {
                        const url = new URL(params?.link ? (params?.link + "/") : (window.location.origin+'/'+env().REACT_APP_ROOT_FOLDER+'/'));
                        url.searchParams.append('roomId', incommingCall.roomId ?? "");
                        url.searchParams.append('sessionId', sessionId ?? "");
                        if (incommingCall.isAudio) {
                            url.searchParams.append('directStart', "true");
                            url.searchParams.append('audioOnly', "true");
                        }
                        const blankUrl = `${url.origin}${url.pathname}${url.search}`;
                        window.open(blankUrl, '_blank');
                    }
                    dispatch(removeModal(modal));
                    dispatch(removeIncommingCall());
                }
            });
            const close: ActionFn = () => {
                stopRinging();
                sendMessageSignal("DeclineCall", {userId: incommingCall.userId});
                dispatch(removeModal(modal));
                dispatch(removeIncommingCall());
            };
            const modal: UiModal = {component, close};
            dispatch(addModal(modal));
            startRinging();
        }
    }, [incommingCall]);

    const sendMessageSignal = (methodName: any, request: any) => {
        if (connection !== null) {
            if (request !== null) {
                connection.invoke(methodName, request);
            } else {
                connection.invoke(methodName);

            }
        }
    }

    const playRing = () => {
        const audioCtx = audioCtxRef.current;
        if (!audioCtx) return;

        const duration = 1.5;
        const ringFrequency = 880;

        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(ringFrequency, audioCtx.currentTime);

        gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);

        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);

        oscillator.start();
        oscillator.stop(audioCtx.currentTime + duration);
    };

    const startRinging = async () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new window.AudioContext();
        }

        if (audioCtxRef.current.state === 'suspended') {
            audioCtxRef.current.resume();
        }

        playRing();
        ringIntervalRef.current = setInterval(playRing, 3000);
    };

    const stopRinging = () => {
        clearInterval(ringIntervalRef.current);
    };
    
    return <></>;
}